import React, { useState } from 'react';
import { useApp, TriageQuestionnaire, Appointment, getSafeUrl } from './AppContext';
import { Button } from './Components';

interface PreConsultationTriageModalProps {
  appointment: Appointment;
  isOpen: boolean;
  onClose: () => void;
  isDoctorView?: boolean;
  onImportToNotes?: (triageText: string) => void;
}

const COMMON_COMPLAINTS = [
  'General Illness & Fever',
  'Cough, Cold & Flu Symptoms',
  'Skin Rash / Dermatological Issue',
  'Headache / Migraine',
  'Stomach & Digestive Issues',
  'Back / Joint / Muscle Pain',
  'Allergies / Sinus Congestion',
  'Prescription Renewal',
  'Mental Health / Anxiety / Sleep',
  'Routine Follow-up'
];

const COMMON_SYMPTOMS = [
  'Fever / Chills',
  'Shortness of Breath',
  'Persistent Cough',
  'Nausea / Vomiting',
  'Dizziness / Lightheadedness',
  'Extreme Fatigue',
  'Body / Muscle Aches',
  'Sore Throat',
  'Loss of Taste / Smell',
  'Chest Tightness (Mild)',
  'Swelling / Inflammation'
];

const COMMON_CONDITIONS = [
  'Hypertension (High BP)',
  'Type 1 or 2 Diabetes',
  'Asthma / Respiratory',
  'Cardiovascular Disease',
  'Thyroid Disorder',
  'Kidney Disease',
  'None / Healthy'
];

export const PreConsultationTriageModal: React.FC<PreConsultationTriageModalProps> = ({
  appointment,
  isOpen,
  onClose,
  isDoctorView = false,
  onImportToNotes
}) => {
  const { user, submitTriageQuestionnaire, uploadClinicalFile, showToast } = useApp();
  const existingTriage = appointment.triage;

  // Form State
  const [chiefComplaint, setChiefComplaint] = useState(existingTriage?.chiefComplaint || '');
  const [customComplaint, setCustomComplaint] = useState('');
  const [symptomDuration, setSymptomDuration] = useState(existingTriage?.symptomDuration || '< 24 Hours');
  const [severity, setSeverity] = useState<number>(existingTriage?.severity || 4);
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>(existingTriage?.associatedSymptoms || []);
  const [currentMedications, setCurrentMedications] = useState(existingTriage?.currentMedications || user?.currentMedications || '');
  const [knownAllergies, setKnownAllergies] = useState(existingTriage?.knownAllergies || user?.allergies || '');
  const [selectedConditions, setSelectedConditions] = useState<string[]>(existingTriage?.pastMedicalConditions || []);
  const [notes, setNotes] = useState(existingTriage?.notes || '');
  const [triageFiles, setTriageFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  if (!isOpen) return null;

  const toggleSymptom = (sym: string) => {
    setSelectedSymptoms(prev =>
      prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym]
    );
  };

  const toggleCondition = (cond: string) => {
    setSelectedConditions(prev =>
      prev.includes(cond) ? prev.filter(c => c !== cond) : [...prev, cond]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalComplaint = customComplaint.trim() ? customComplaint.trim() : chiefComplaint;
    if (!finalComplaint) {
      showToast('Validation Error', 'Please select or enter the main reason for your visit.', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      let attachmentUrl = existingTriage?.attachmentUrl;
      let attachmentName = existingTriage?.attachmentName;
      let attachments = existingTriage?.attachments || [];

      if (triageFiles.length > 0) {
        const uploadResults = await Promise.all(triageFiles.map(f => uploadClinicalFile(f)));
        attachmentUrl = uploadResults[0]?.url;
        attachmentName = uploadResults[0]?.name;
        attachments = uploadResults.map((r, i) => ({
          name: r.name,
          url: r.url,
          size: triageFiles[i]?.size,
          type: triageFiles[i]?.type
        }));
      }

      const triagePayload: TriageQuestionnaire = {
        completedAt: new Date().toISOString(),
        chiefComplaint: finalComplaint,
        symptomDuration,
        severity,
        associatedSymptoms: selectedSymptoms,
        currentMedications: currentMedications.trim(),
        knownAllergies: knownAllergies.trim(),
        pastMedicalConditions: selectedConditions,
        isUrgentFlagged: severity >= 8,
        notes: notes.trim(),
        attachmentUrl,
        attachmentName,
        attachments: attachments.length > 0 ? attachments : undefined
      };

      await submitTriageQuestionnaire(appointment.id, triagePayload);
      onClose();
    } catch (err: any) {
      console.error("Triage submission error:", err);
      showToast('Submission Error', err?.message || 'Failed to submit triage questionnaire.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSeverityBadge = (level: number) => {
    if (level <= 3) return { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Mild / Low Severity' };
    if (level <= 6) return { bg: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Moderate Symptoms' };
    if (level <= 8) return { bg: 'bg-orange-50 text-orange-700 border-orange-200', label: 'High / Severe Discomfort' };
    return { bg: 'bg-rose-50 text-rose-700 border-rose-200', label: 'Critical / Acute Severity' };
  };

  const handleCopyTriageSummary = () => {
    if (!existingTriage) return;
    const summary = `[PRE-VISIT TRIAGE SUMMARY]\n• Chief Complaint: ${existingTriage.chiefComplaint}\n• Duration: ${existingTriage.symptomDuration}\n• Severity Score: ${existingTriage.severity}/10\n• Associated Symptoms: ${existingTriage.associatedSymptoms?.join(', ') || 'None reported'}\n• Current Meds: ${existingTriage.currentMedications || 'None'}\n• Allergies: ${existingTriage.knownAllergies || 'None'}\n• Past Conditions: ${existingTriage.pastMedicalConditions?.join(', ') || 'None'}\n• Patient Context: ${existingTriage.notes || 'None'}`;
    
    if (onImportToNotes) {
      onImportToNotes(summary);
      showToast('Triage Imported', 'Triage clinical summary copied directly into record form.', 'success');
    } else {
      navigator.clipboard.writeText(summary);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
      showToast('Copied', 'Triage summary copied to clipboard.', 'info');
    }
  };

  return (
    <div className="fixed inset-0 z-[250] bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-300 font-['Plus_Jakarta_Sans']">
      <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[90vh] my-auto">
        
        {/* Modal Header */}
        <div className="p-6 md:p-8 bg-gradient-to-r from-indigo-900 via-indigo-800 to-slate-900 text-white flex justify-between items-start shrink-0">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                {isDoctorView ? 'Clinical Triage Review' : 'Pre-Consultation Clinical Triage'}
              </span>
              {existingTriage?.isUrgentFlagged && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest bg-rose-500 text-white animate-pulse">
                  High Severity Alert
                </span>
              )}
            </div>
            <h2 className="text-xl md:text-2xl font-black tracking-tight text-white">
              {isDoctorView ? `Triage Form: ${appointment.patientName}` : 'Tell Your Doctor How You Are Feeling'}
            </h2>
            <p className="text-xs text-indigo-200 font-medium">
              {isDoctorView 
                ? `Submitted by patient for session on ${appointment.date} @ ${appointment.time}`
                : `Sharing your symptoms in advance helps Dr. ${appointment.doctorName} prepare for a more efficient consultation.`}
            </p>
          </div>
          <button 
            onClick={onClose}
            aria-label="Close Triage Modal"
            className="w-10 h-10 rounded-2xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all shrink-0 ml-4"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 md:p-8 overflow-y-auto space-y-8 flex-grow scrollbar-thin text-slate-800">
          
          {/* DOCTOR VIEW MODE */}
          {isDoctorView ? (
            <div className="space-y-6">
              {!existingTriage ? (
                <div className="p-12 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200 space-y-3">
                  <div className="w-14 h-14 bg-indigo-50 text-indigo-500 rounded-2xl flex items-center justify-center mx-auto text-xl">
                    <i className="fa-solid fa-clipboard-question"></i>
                  </div>
                  <h4 className="font-black text-slate-700 text-base">Triage Questionnaire Not Submitted Yet</h4>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    The patient has not completed the pre-consultation triage questionnaire for this visit. You can take their clinical history directly during the call.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Severity & Chief Complaint Highlight Card */}
                  <div className="p-6 bg-slate-50 rounded-3xl border border-slate-100 space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-4">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Chief Complaint / Primary Concern</p>
                        <h3 className="text-lg md:text-xl font-black text-indigo-950 mt-0.5">{existingTriage.chiefComplaint}</h3>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-black border ${getSeverityBadge(existingTriage.severity).bg}`}>
                          <i className="fa-solid fa-gauge-high text-xs"></i>
                          Severity: {existingTriage.severity}/10 ({getSeverityBadge(existingTriage.severity).label})
                        </span>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 text-xs font-medium pt-1">
                      <div>
                        <span className="text-slate-400 font-bold block text-[10px] uppercase tracking-wider">Symptom Duration:</span>
                        <span className="font-bold text-slate-800 text-sm">{existingTriage.symptomDuration}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 font-bold block text-[10px] uppercase tracking-wider">Form Completed At:</span>
                        <span className="text-slate-600 font-semibold">{new Date(existingTriage.completedAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Associated Symptoms */}
                  <div className="space-y-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600">Reported Associated Symptoms</p>
                    <div className="flex flex-wrap gap-2">
                      {existingTriage.associatedSymptoms && existingTriage.associatedSymptoms.length > 0 ? (
                        existingTriage.associatedSymptoms.map((sym, i) => (
                          <span key={i} className="px-3 py-1.5 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-xl text-xs font-bold flex items-center gap-1.5">
                            <i className="fa-solid fa-check text-[10px] text-indigo-500"></i>
                            {sym}
                          </span>
                        ))
                      ) : (
                        <p className="text-xs text-slate-400 italic">No secondary symptoms selected.</p>
                      )}
                    </div>
                  </div>

                  {/* Medications & Allergies Grid */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1.5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                        <i className="fa-solid fa-pills text-indigo-500"></i> Current Medications
                      </p>
                      <p className="text-xs font-medium text-slate-700 whitespace-pre-wrap">
                        {existingTriage.currentMedications || 'None reported'}
                      </p>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-1.5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-rose-400 flex items-center gap-1.5">
                        <i className="fa-solid fa-triangle-exclamation text-rose-500"></i> Known Allergies
                      </p>
                      <p className="text-xs font-bold text-rose-600 whitespace-pre-wrap">
                        {existingTriage.knownAllergies || 'No known allergies'}
                      </p>
                    </div>
                  </div>

                  {/* Pre-existing Conditions */}
                  {existingTriage.pastMedicalConditions && existingTriage.pastMedicalConditions.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Pre-existing Medical Conditions</p>
                      <div className="flex flex-wrap gap-2">
                        {existingTriage.pastMedicalConditions.map((cond, i) => (
                          <span key={i} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-xs font-semibold">
                            {cond}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Patient's Notes / Direct Words */}
                  {existingTriage.notes && (
                    <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100 space-y-1">
                      <p className="text-[10px] font-black uppercase tracking-widest text-indigo-700">Patient Detailed Description</p>
                      <p className="text-xs text-slate-700 leading-relaxed font-medium whitespace-pre-wrap italic">
                        "{existingTriage.notes}"
                      </p>
                    </div>
                  )}

                  {/* Attachment if present */}
                  {existingTriage.attachmentUrl && (
                    <div className="p-4 bg-white rounded-2xl border border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                          <i className="fa-solid fa-paperclip"></i>
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">{existingTriage.attachmentName || 'Patient Attachment'}</p>
                          <p className="text-[10px] text-slate-400">Uploaded with triage</p>
                        </div>
                      </div>
                      <a 
                        href={getSafeUrl(existingTriage.attachmentUrl)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all"
                      >
                        <i className="fa-solid fa-arrow-up-right-from-square"></i> Open File
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* PATIENT INTERACTIVE FORM MODE */
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Emergency Banner */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-3 text-xs text-amber-900">
                <i className="fa-solid fa-triangle-exclamation text-amber-600 text-base shrink-0 mt-0.5"></i>
                <div>
                  <span className="font-black block">Medical Emergency Notice</span>
                  If you are experiencing chest pain, severe shortness of breath, sudden facial drooping, or uncontrolled bleeding, please call emergency services (911/112) immediately.
                </div>
              </div>

              {/* 1. Chief Complaint */}
              <div className="space-y-3">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                  1. What is the main reason for your consultation today? <span className="text-rose-500">*</span>
                </label>
                <div className="grid sm:grid-cols-2 gap-2">
                  {COMMON_COMPLAINTS.map(comp => (
                    <button
                      type="button"
                      key={comp}
                      onClick={() => { setChiefComplaint(comp); setCustomComplaint(''); }}
                      className={`p-3 rounded-2xl text-xs font-bold text-left transition-all border ${
                        chiefComplaint === comp && !customComplaint
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-500/20'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200/70'
                      }`}
                    >
                      {comp}
                    </button>
                  ))}
                </div>
                <div className="pt-1">
                  <input
                    type="text"
                    value={customComplaint}
                    onChange={(e) => { setCustomComplaint(e.target.value); setChiefComplaint(''); }}
                    placeholder="Or type a specific reason (e.g., ear ache, knee swelling, rash on left arm)..."
                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>

              {/* 2. Symptom Duration & Severity Scale */}
              <div className="grid sm:grid-cols-2 gap-6 pt-2">
                <div className="space-y-2">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                    2. How long have you had these symptoms?
                  </label>
                  <select
                    value={symptomDuration}
                    onChange={(e) => setSymptomDuration(e.target.value)}
                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="< 24 Hours">Less than 24 hours</option>
                    <option value="1 to 3 Days">1 to 3 days</option>
                    <option value="4 to 7 Days">4 to 7 days (About a week)</option>
                    <option value="1 to 4 Weeks">1 to 4 weeks</option>
                    <option value="> 1 Month">More than a month (Chronic)</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                      3. Discomfort / Severity Level (1 - 10)
                    </label>
                    <span className={`text-xs font-black px-2 py-0.5 rounded-lg ${getSeverityBadge(severity).bg}`}>
                      {severity}/10 - {severity <= 3 ? 'Mild' : severity <= 6 ? 'Moderate' : severity <= 8 ? 'High' : 'Severe'}
                    </span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={severity}
                    onChange={(e) => setSeverity(parseInt(e.target.value))}
                    className="w-full accent-indigo-600 cursor-pointer h-2 bg-slate-200 rounded-lg"
                  />
                  <div className="flex justify-between text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    <span>1 (Very Mild)</span>
                    <span>5 (Moderate)</span>
                    <span>10 (Severe Pain)</span>
                  </div>
                </div>
              </div>

              {/* 3. Associated Symptoms */}
              <div className="space-y-2.5 pt-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                  4. Are you experiencing any of these associated symptoms? (Select all that apply)
                </label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_SYMPTOMS.map(sym => {
                    const isSelected = selectedSymptoms.includes(sym);
                    return (
                      <button
                        type="button"
                        key={sym}
                        onClick={() => toggleSymptom(sym)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all border ${
                          isSelected
                            ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <i className={`fa-solid ${isSelected ? 'fa-square-check text-indigo-600' : 'fa-square text-slate-300'} mr-1.5`}></i>
                        {sym}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 4. Medications & Allergies */}
              <div className="grid sm:grid-cols-2 gap-4 pt-2">
                <div className="space-y-1.5">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                    5. Current Medications & Doses
                  </label>
                  <input
                    type="text"
                    value={currentMedications}
                    onChange={(e) => setCurrentMedications(e.target.value)}
                    placeholder="e.g. Paracetamol 500mg, Metformin 850mg, none"
                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                    6. Known Drug Allergies
                  </label>
                  <input
                    type="text"
                    value={knownAllergies}
                    onChange={(e) => setKnownAllergies(e.target.value)}
                    placeholder="e.g. Penicillin, Sulfa, Aspirin, or None"
                    className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500 text-rose-600 font-semibold placeholder:text-slate-400 placeholder:font-normal"
                  />
                </div>
              </div>

              {/* 5. Pre-existing Conditions */}
              <div className="space-y-2 pt-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                  7. Any chronic or pre-existing medical conditions?
                </label>
                <div className="flex flex-wrap gap-2">
                  {COMMON_CONDITIONS.map(cond => {
                    const isSelected = selectedConditions.includes(cond);
                    return (
                      <button
                        type="button"
                        key={cond}
                        onClick={() => toggleCondition(cond)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all border ${
                          isSelected
                            ? 'bg-slate-900 border-slate-900 text-white'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        {cond}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 6. Detailed notes & questions */}
              <div className="space-y-1.5 pt-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                  8. Additional context or questions for your doctor
                </label>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Describe your symptoms in your own words, what makes it better/worse, or specific questions you'd like to ask..."
                  className="w-full p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-medium outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* 7. Upload Photo or Document */}
              <div className="space-y-1.5 pt-2">
                <label className="block text-xs font-black uppercase tracking-wider text-slate-700">
                  9. Optional: Attach photos of symptom / recent lab results (Multiple Supported)
                </label>
                <div className="p-4 border-2 border-dashed border-slate-200 hover:border-indigo-400 rounded-2xl bg-slate-50 transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-cloud-arrow-up text-xl text-indigo-500"></i>
                    <div>
                      <p className="text-xs font-bold text-slate-700">
                        {triageFiles.length > 0
                          ? `${triageFiles.length} file(s) selected`
                          : (existingTriage?.attachmentName || 'Attach files or images (PNG, JPG, PDF)')}
                      </p>
                      <p className="text-[10px] text-slate-400">PDF, PNG, JPG up to 10MB each</p>
                    </div>
                  </div>
                  <label className="px-4 py-2 bg-white border border-slate-200 hover:bg-indigo-50 hover:border-indigo-200 text-indigo-600 rounded-xl text-xs font-bold cursor-pointer transition-all shrink-0">
                    <span>{triageFiles.length > 0 ? 'Add More Files' : 'Select Files'}</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const newFiles = Array.from(e.target.files);
                          setTriageFiles(prev => [...prev, ...newFiles]);
                        }
                      }}
                    />
                  </label>
                </div>

                {triageFiles.length > 0 && (
                  <div className="space-y-1.5 max-h-32 overflow-y-auto scrollbar-thin mt-2">
                    {triageFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-xl border border-slate-200 text-xs">
                        <span className="font-bold text-slate-700 truncate" title={file.name}>
                          <i className="fa-solid fa-paperclip text-indigo-500 mr-1.5"></i>
                          {file.name} ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => setTriageFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-rose-600 px-1.5 py-0.5 ml-2"
                          title="Remove file"
                        >
                          <i className="fa-solid fa-xmark"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-4 text-sm font-bold shadow-lg shadow-indigo-600/25"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="fa-solid fa-circle-notch fa-spin"></i> Saving Triage...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <i className="fa-solid fa-clipboard-check"></i>
                      {existingTriage ? 'Update Pre-Consultation Triage' : 'Submit & Share With Doctor'}
                    </span>
                  )}
                </Button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-4 rounded-2xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Modal Footer (for Doctor view) */}
        {isDoctorView && existingTriage && (
          <div className="p-4 md:p-6 bg-slate-50 border-t border-slate-100 flex flex-wrap justify-between items-center gap-4 shrink-0">
            <span className="text-xs text-slate-500 font-medium">
              <i className="fa-solid fa-shield-halved text-indigo-600 mr-1.5"></i> Encrypted clinical transmission
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCopyTriageSummary}
                className="px-4 py-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-xl text-xs font-bold transition-all flex items-center gap-2"
              >
                <i className={`fa-solid ${isCopied ? 'fa-check text-emerald-600' : 'fa-clipboard-list'}`}></i>
                {onImportToNotes ? 'Import into Clinical Notes' : (isCopied ? 'Copied!' : 'Copy Summary')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
